import React from 'react';
import { ExternalLink, Newspaper } from 'lucide-react';
import { formatNewsDate, newsSourceLabel } from '../../utils/companyNews';

/**
 * Press coverage for one issuer, from json_data.basic_company_details.company_news.
 *
 * Every story links out to its publisher: the backend stores a headline, a
 * summary line and the source's own URL, never the article itself.
 */
export const CompanyNewsPanel = ({ company, news = [] }) => (
  <div className="space-y-6">
    <div className="bg-white rounded-3xl border border-slate-200/90 p-6 sm:p-8 shadow-xs space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="flex items-center gap-2 text-lg sm:text-xl font-extrabold text-slate-900">
            <Newspaper className="w-5 h-5 text-[#008080]" aria-hidden="true" />
            In the News
          </h3>
          <p className="mt-1 text-xs sm:text-sm text-slate-500">
            Coverage of {company || 'this company'} from the press, collected automatically.
          </p>
        </div>
        {news.length > 0 && (
          <span className="shrink-0 px-2.5 py-1 rounded-full bg-slate-100 text-[11px] font-bold text-slate-600">
            {news.length} {news.length === 1 ? 'story' : 'stories'}
          </span>
        )}
      </div>

      {news.length === 0 ? (
        <div className="py-10 text-center">
          <Newspaper className="w-9 h-9 mx-auto text-slate-300" aria-hidden="true" />
          <p className="mt-3 text-sm font-bold text-slate-700">No press coverage yet</p>
          <p className="mt-1 text-xs text-slate-500">
            Stories appear here once the issuer is written about by name.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {news.map((item) => (
            <li key={item.url} className="py-4 first:pt-0 last:pb-0">
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-start gap-3 sm:gap-4"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-sm sm:text-base font-bold text-slate-900 group-hover:text-[#008080] leading-snug transition-colors">
                    {item.title}
                  </span>
                  {item.body && (
                    <span className="mt-1.5 block text-xs sm:text-[13px] text-slate-500 leading-relaxed line-clamp-2">
                      {item.body}
                    </span>
                  )}
                  <span className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-semibold text-slate-400">
                    {newsSourceLabel(item.source) && (
                      <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">{newsSourceLabel(item.source)}</span>
                    )}
                    {formatNewsDate(item.date) && <span>{formatNewsDate(item.date)}</span>}
                  </span>
                </span>
                <ExternalLink
                  className="w-4 h-4 mt-1 text-slate-300 group-hover:text-[#008080] shrink-0 transition-colors"
                  aria-hidden="true"
                />
              </a>
            </li>
          ))}
        </ul>
      )}

      <p className="pt-1 text-[11px] text-slate-400 leading-relaxed">
        Headlines are gathered by an automated search and link to the publisher. Alambrix does not write or endorse them.
      </p>
    </div>
  </div>
);

export default CompanyNewsPanel;
